/* Search Performance (Google Search Console) table model — fix/gsc-table-filters
 * (02-11-2026). PURE and DOM-free (tested with `node --test
 * --experimental-strip-types`); no '@/' imports so the tests load it directly.
 *
 * Why it exists (owner screenshot 02-11-2026): the GSC table showed the same
 * query on several rows and sorting looked broken. Two causes:
 *  1. the API returned rows from EVERY stored sync window (fixed server-side:
 *     the list is now fenced to one window and aggregated by query);
 *  2. the table keyed rows by the query text alone, so duplicate queries gave
 *     React duplicate keys — on re-sort React reused the wrong DOM rows, which
 *     shows ghost rows with identical numbers and an apparently unsorted table.
 * This model keeps the client honest even if an old API answers: rows are
 * de-duplicated per window, keys are unique, and the search/preset state is
 * plain data the page can put in the URL. */

/** Structural twin of SeoSearchQueryRow (sources.ts) — kept local for tests. */
export interface GscTableRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  date_start: string;
  date_end: string;
  source: string;
}

/** Date presets, mirroring Google's Performance page. Server tokens. */
export type GscPreset = '1d' | '7d' | '28d' | '3m';
export const GSC_PRESETS: { token: GscPreset; label: string; hint: string }[] = [
  { token: '1d', label: 'Last day', hint: 'The latest complete day Google has finalised (about 2 days ago). Google’s own “24 hours” view uses unfinalised data.' },
  { token: '7d', label: '7 days', hint: 'The last 7 finalised days' },
  { token: '28d', label: '28 days', hint: 'The last 28 finalised days' },
  { token: '3m', label: '3 months', hint: 'The last 3 months of finalised days' },
];
export const DEFAULT_PRESET: GscPreset = '28d';

export function isPreset(v: unknown): v is GscPreset {
  return v === '1d' || v === '7d' || v === '28d' || v === '3m';
}

/** URL value -> preset (anything unknown -> the 28-day default). */
export function presetFromParam(v: string | null | undefined): GscPreset {
  return isPreset(v) ? v : DEFAULT_PRESET;
}

/** Unique, stable React key: query + its window (never the query alone). */
export function gscRowKey(r: GscTableRow): string {
  return `${r.query}\u0000${r.date_start}\u0000${r.date_end}`;
}

const normQuery = (q: string): string => q.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Aggregate rows by query WITHIN one window (case/space-insensitive): clicks and
 * impressions summed, CTR = clicks / impressions, position weighted by
 * impressions (plain mean with no impressions). Rows from different windows
 * are never merged: when an older API returns several windows, only the
 * window with the latest end date (then latest start) is kept, because the
 * 28-day windows overlap and summing them would double-count.
 */
export function aggregateGscRows(rows: GscTableRow[]): GscTableRow[] {
  if (rows.length === 0) return [];
  let keep = rows[0];
  for (const r of rows) {
    if (r.date_end > keep.date_end || (r.date_end === keep.date_end && r.date_start > keep.date_start)) keep = r;
  }
  const inWindow = rows.filter((r) => r.date_start === keep.date_start && r.date_end === keep.date_end);
  const acc = new Map<string, { row: GscTableRow; clicks: number; impr: number; wPos: number; sPos: number; n: number }>();
  for (const r of inWindow) {
    const k = normQuery(r.query);
    if (k === '') continue;
    const a = acc.get(k) ?? { row: { ...r, query: r.query.trim() }, clicks: 0, impr: 0, wPos: 0, sPos: 0, n: 0 };
    a.clicks += r.clicks;
    a.impr += r.impressions;
    a.wPos += r.position * r.impressions;
    a.sPos += r.position;
    a.n += 1;
    acc.set(k, a);
  }
  return [...acc.values()].map((a) => ({
    ...a.row,
    clicks: a.clicks,
    impressions: a.impr,
    ctr: a.impr > 0 ? Math.round((a.clicks / a.impr) * 10000) / 10000 : 0,
    position: a.impr > 0 ? Math.round((a.wPos / a.impr) * 100) / 100 : a.n > 0 ? Math.round((a.sPos / a.n) * 100) / 100 : 0,
  }));
}

/** Case-insensitive "contains" search over the query text (all words must match). */
export function filterGscRows(rows: GscTableRow[], search: string): GscTableRow[] {
  const words = normQuery(search).split(' ').filter(Boolean);
  if (words.length === 0) return rows;
  return rows.filter((r) => {
    const q = normQuery(r.query);
    return words.every((w) => q.includes(w));
  });
}

/** 'YYYY-MM-DD' -> 'DD-MM-YYYY' (UK) without Date parsing (WebKit-safe). */
export function ukDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}

/** Totals across the given rows: clicks, impressions, CTR, impression-weighted position. */
export function gscTotals(rows: GscTableRow[]): { clicks: number; impressions: number; ctr: number | null; position: number | null } {
  const clicks = rows.reduce((s, r) => s + r.clicks, 0);
  const impressions = rows.reduce((s, r) => s + r.impressions, 0);
  return {
    clicks,
    impressions,
    ctr: impressions > 0 ? clicks / impressions : null,
    position: impressions > 0 ? rows.reduce((s, r) => s + r.position * r.impressions, 0) / impressions : null,
  };
}
